/**
 * Wire shadow store — retains full Device Sync trip/city bodies so web edits
 * merge into last-known iOS payloads instead of rebuilding sparse objects.
 */

const TRIP_SHADOW_PREFIX = 'wt_device_sync_trip_shadow_';
const CITY_SHADOW_PREFIX = 'wt_device_sync_city_shadow_';

function tripKey(userId: string, tripId: string): string {
  return `${TRIP_SHADOW_PREFIX}${userId}:${tripId}`;
}

function cityKey(userId: string, cityVisitId: string): string {
  return `${CITY_SHADOW_PREFIX}${userId}:${cityVisitId}`;
}

function readJson(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function writeJson(key: string, body: Record<string, unknown>): void {
  localStorage.setItem(key, JSON.stringify(body));
}

export function getTripWireShadow(
  userId: string,
  tripId: string
): Record<string, unknown> | null {
  return readJson(tripKey(userId, tripId));
}

export function setTripWireShadow(
  userId: string,
  tripId: string,
  body: Record<string, unknown>
): void {
  writeJson(tripKey(userId, tripId), body);
}

export function getCityWireShadow(
  userId: string,
  cityVisitId: string
): Record<string, unknown> | null {
  return readJson(cityKey(userId, cityVisitId));
}

export function setCityWireShadow(
  userId: string,
  cityVisitId: string,
  body: Record<string, unknown>
): void {
  writeJson(cityKey(userId, cityVisitId), body);
}

export function deleteCityWireShadow(userId: string, cityVisitId: string): void {
  localStorage.removeItem(cityKey(userId, cityVisitId));
}

export function deleteTripWireShadow(userId: string, tripId: string): void {
  localStorage.removeItem(tripKey(userId, tripId));
}

/** Deep-merge objects; arrays from `overlay` replace when provided. */
export function mergeWireBodies(
  base: Record<string, unknown> | null | undefined,
  overlay: Record<string, unknown>
): Record<string, unknown> {
  if (!base) return { ...overlay };
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    if (value === undefined) continue;
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      out[key] &&
      typeof out[key] === 'object' &&
      !Array.isArray(out[key])
    ) {
      out[key] = mergeWireBodies(
        out[key] as Record<string, unknown>,
        value as Record<string, unknown>
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Merge web-edited days/items into a shadowed city body while preserving
 * hotelStays, largeCityBlobs, attachmentRefs, and unmatched item wire fields.
 */
export function mergeCityVisitWireWithWebEdits(
  shadow: Record<string, unknown> | null,
  webBody: Record<string, unknown>
): Record<string, unknown> {
  const merged = mergeWireBodies(shadow, webBody);

  // Preserve hotelStays / blobs / file refs from shadow when web sent empties.
  if (shadow) {
    const shadowHotels = shadow.hotelStays;
    const webHotels = webBody.hotelStays;
    if (
      Array.isArray(shadowHotels) &&
      shadowHotels.length > 0 &&
      Array.isArray(webHotels) &&
      webHotels.length === 0
    ) {
      merged.hotelStays = shadowHotels;
    }

    const shadowOptions = shadow.options;
    const webOptions = webBody.options;
    if (
      Array.isArray(shadowOptions) &&
      shadowOptions.length > 0 &&
      Array.isArray(webOptions) &&
      webOptions.length === 0
    ) {
      merged.options = shadowOptions;
    }

    if (shadow.largeCityBlobs && !webBody.largeCityBlobs) {
      merged.largeCityBlobs = shadow.largeCityBlobs;
    }
    if (
      Array.isArray(shadow.attachmentFileIds) &&
      (shadow.attachmentFileIds as unknown[]).length > 0 &&
      Array.isArray(webBody.attachmentFileIds) &&
      (webBody.attachmentFileIds as unknown[]).length === 0
    ) {
      merged.attachmentFileIds = shadow.attachmentFileIds;
    }
    if (
      Array.isArray(shadow.attachmentRefs) &&
      (shadow.attachmentRefs as unknown[]).length > 0 &&
      !Array.isArray(webBody.attachmentRefs)
    ) {
      merged.attachmentRefs = shadow.attachmentRefs;
    }

    // Preserve country / timezone when web omit
    for (const key of [
      'timeZoneIdentifier',
      'countryName',
      'countryISO2',
      'currencyCode',
    ] as const) {
      if (merged[key] == null && shadow[key] != null) {
        merged[key] = shadow[key];
      }
    }
  }

  // Merge days: keep item-level wire fields for same ids
  const shadowDays = Array.isArray(shadow?.days) ? (shadow!.days as unknown[]) : [];
  const webDays = Array.isArray(webBody.days) ? (webBody.days as unknown[]) : [];
  const daysToMerge = webDays.length === 0 && shadowDays.length > 0 ? shadowDays : webDays;
  if (webDays.length > 0 || shadowDays.length > 0) {
    const shadowDayById = new Map<string, Record<string, unknown>>();
    for (const d of shadowDays) {
      if (d && typeof d === 'object') {
        const day = d as Record<string, unknown>;
        const dayId = String(day.id ?? '');
        const dayDate = String(day.date ?? '');
        if (dayId) shadowDayById.set(dayId, day);
        if (dayDate) shadowDayById.set(dayDate, day);
      }
    }

    merged.days = daysToMerge.map((d) => {
      if (!d || typeof d !== 'object') return d;
      const webDay = d as Record<string, unknown>;
      const dayId = String(webDay.id ?? '');
      const dayDate = String(webDay.date ?? '');
      const shadowDay =
        (dayId ? shadowDayById.get(dayId) : undefined) ??
        (dayDate ? shadowDayById.get(dayDate) : undefined);
      const dayMerged = mergeWireBodies(shadowDay, webDay);
      if (shadowDay?.id && shadowDay.id !== webDay.id) {
        dayMerged.id = shadowDay.id;
      }

      const shadowItems = Array.isArray(shadowDay?.items)
        ? (shadowDay!.items as unknown[])
        : [];
      const webItems = Array.isArray(webDay.items) ? (webDay.items as unknown[]) : [];
      const itemsToMerge =
        webItems.length === 0 && shadowItems.length > 0 ? shadowItems : webItems;
      const shadowItemById = new Map<string, Record<string, unknown>>();
      for (const it of shadowItems) {
        if (it && typeof it === 'object') {
          const item = it as Record<string, unknown>;
          shadowItemById.set(String(item.id ?? ''), item);
        }
      }

      dayMerged.items = itemsToMerge.map((it) => {
        if (!it || typeof it !== 'object') return it;
        const webItem = it as Record<string, unknown>;
        const prev = shadowItemById.get(String(webItem.id ?? ''));
        if (!prev) return webItem;
        // Overlay web fields; keep barcode/reminders/attachments/metadata if web omitted nulls
        const itemMerged = { ...prev, ...webItem };
        for (const k of [
          'barcode',
          'reminders',
          'attachments',
          'options',
          'metadata',
          'provider',
          'email',
          'reservationUrl',
          'reservationRequirement',
          'price',
          'currency',
          'priorityScore',
          'timeZone',
        ] as const) {
          if (
            (webItem[k] == null ||
              (Array.isArray(webItem[k]) && (webItem[k] as unknown[]).length === 0) ||
              (typeof webItem[k] === 'object' &&
                !Array.isArray(webItem[k]) &&
                Object.keys(webItem[k] as object).length === 0)) &&
            prev[k] != null
          ) {
            // Only restore when web sent empty defaults and shadow had real data
            if (k === 'barcode' && webItem.barcode === null && prev.barcode) {
              itemMerged.barcode = prev.barcode;
            } else if (
              (k === 'reminders' || k === 'attachments' || k === 'options') &&
              Array.isArray(webItem[k]) &&
              (webItem[k] as unknown[]).length === 0 &&
              Array.isArray(prev[k]) &&
              (prev[k] as unknown[]).length > 0
            ) {
              itemMerged[k] = prev[k];
            } else if (
              k === 'metadata' &&
              webItem.metadata &&
              typeof webItem.metadata === 'object' &&
              Object.keys(webItem.metadata as object).length === 0 &&
              prev.metadata &&
              typeof prev.metadata === 'object' &&
              Object.keys(prev.metadata as object).length > 0
            ) {
              itemMerged.metadata = prev.metadata;
            } else if (
              [
                'provider',
                'email',
                'reservationUrl',
                'reservationRequirement',
                'price',
                'currency',
                'priorityScore',
                'timeZone',
              ].includes(k) &&
              webItem[k] == null &&
              prev[k] != null
            ) {
              itemMerged[k] = prev[k];
            }
          }
        }
        // Preserve location.id from shadow when web minted a new one without coords change
        if (
          prev.location &&
          typeof prev.location === 'object' &&
          webItem.location &&
          typeof webItem.location === 'object'
        ) {
          const prevLoc = prev.location as Record<string, unknown>;
          const webLoc = webItem.location as Record<string, unknown>;
          itemMerged.location = {
            ...prevLoc,
            ...webLoc,
            id: prevLoc.id ?? webLoc.id,
          };
        }
        return itemMerged;
      });

      if (shadowDay?.notes != null && (webDay.notes == null || webDay.notes === '')) {
        dayMerged.notes = shadowDay.notes;
      }
      if (shadowDay?.timeZone != null && webDay.timeZone == null) {
        dayMerged.timeZone = shadowDay.timeZone;
      }
      return dayMerged;
    });
  }

  return merged;
}
